import { isAxiosError } from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { v4 as uuid } from 'uuid';
import {
  computed,
  reactive,
  ref,
  watch,
} from 'vue';

import type {
  CreateConversationMessage200Response,
  MessageAttachmentDTO,
  MessageCreatePayload,
  MessageResultDTO,
  MessageResultV2DTO,
  MessageUpdatePayload,
  ReactionAggregateResultDTO,
  ReactionCreatePayload,
  ReactionResultDTO,
} from '@nora-monorepo/nora-api-client';
import { appLogger, useApiStore } from '@nora-monorepo/ui-shared';

import type { EditMessage, MessageInput, MessageState } from '#nora-chat-ui/states';
import { useAttachmentStore } from '#nora-chat-ui/stores/attachment-store';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useScrollStore } from '#nora-chat-ui/stores/scroll-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { aggregateReactions } from '#nora-chat-ui/utils/aggregate-reactions';
import { convertToMessageResultV2 } from '#nora-chat-ui/utils/message-dto-adapter';

const MARK_AS_READ_TIMEOUT = 1500;

interface TimerList {
  [key: string]: ReturnType<typeof setTimeout>;
}

export const useConversationMessageStore = defineStore(
  'conversationMessageStore',
  () => {
    const quoteMessage = ref<MessageState>();
    const editingMessage = ref<EditMessage>();

    const userStore = useChatUserStore();

    const { user } = storeToRefs(userStore);

    const conversationStore = useConversationStore();
    const api = useApiStore();
    const filesApiStore = useAttachmentStore();
    const scrollStore = useScrollStore();

    const markAsReadTimers: TimerList = {};
    const pendingReactionMessageIds = reactive(new Set<string>());

    const activeMessageActionsId = ref<string>();
    const openContextMenuId = ref<string>();

    const setActiveMessageActionsId = (id?: string): void => {
      activeMessageActionsId.value = id;
    };

    const setOpenContextMenuId = (id?: string): void => {
      openContextMenuId.value = id;
    };

    // Initialize scroll state for open conversations
    watch(
      () => conversationStore.getOpenConversations,
      (openConversations) => {
        if (!openConversations) {
          return;
        }

        scrollStore.initializeScrollStateForConversations(openConversations);
      },
      {
        immediate: true,
      },
    );

    const getConversationMessage = computed(() => {
      return (conversationId: string, messageId: string) => {
        const conversation = conversationStore.getConversation(conversationId);

        if (!conversation) {
          return undefined;
        }

        return conversation.messages.find(
          messageState => messageState.message.id === messageId,
        );
      };
    });

    function createMessageObject(
      conversationId: string,
      message?: string | null,
      forwardedFromId?: string,
      attachments?: MessageAttachmentDTO[],
    ): MessageResultV2DTO {
      if (!user.value) {
        throw new Error('Tried to create a message without a loaded user.');
      }

      let quotedFrom: MessageResultDTO | undefined;

      if (quoteMessage.value) {
        quotedFrom = {
          id: quoteMessage.value.message.id,
          message: quoteMessage.value.message.message,
          sentBy: quoteMessage.value.message.sentBy,
          createdAt: quoteMessage.value.message.createdAt,
          sentByUserId: quoteMessage.value.message.sentBy.id,
          conversationId: quoteMessage.value.message.conversationId,
          isEdited: quoteMessage.value.message.isEdited ?? false,
        };
      }

      const messageData: MessageResultDTO = {
        id: uuid(),
        message,
        conversationId,
        sentByUserId: user.value.id,
        sentBy: user.value,
        createdAt: new Date(),
        updatedAt: undefined,
        deletedAt: undefined,
        reactions: [],
        readMessages: [],
        quotedFrom,
        forwardedFromId,
        isEdited: false,
        attachments: attachments?.map(attachment => ({
          ...attachment,
          size: `${attachment.size}`,
        })),
      };

      return convertToMessageResultV2(messageData);
    }

    async function createMessage(
      conversationId: string,
      message: string,
      forwardedFromId?: string,
      attachments?: MessageAttachmentDTO[],
    ): Promise<void> {
      if (attachments?.length && attachments?.length > 0) {
        filesApiStore.clearUploadsForConversation(conversationId);
      }

      const newMessage: MessageState = {
        message: createMessageObject(
          conversationId,
          message,
          forwardedFromId,
          attachments,
        ),
        status: 'sending',
      };

      conversationStore.addMessageToConversation(conversationId, newMessage);

      quoteMessage.value = undefined;
      await sendMessage(newMessage);
    }

    async function sendMessage(messageState: MessageState): Promise<void> {
      const messageAtSend = { ...messageState.message };
      const { conversationId, id, message, quotedFrom, forwardedFromId, attachments }
        = messageState.message;
      const payload: MessageCreatePayload = {
        id,
        message,
        quotedMessageId: quotedFrom?.id,
        forwardedFromMessageId: forwardedFromId ?? undefined,
        attachments,
      };

      try {
        messageState.status = 'sending';
        conversationStore.updateMessage(messageState);
        let response: CreateConversationMessage200Response;
        try {
          response = await api.unwrap(
            api.conversationMessage.createConversationMessage({
              conversationId,
              messageCreatePayload: payload,
            }),
          );
        }
        catch (error) {
          if (!isAxiosError(error) || error.response?.status !== 409) {
            throw error;
          }

          // The original request may have been saved before its response was lost.
          response = await api.unwrap(api.conversationMessage.getConversationMessage({
            conversationId,
            messageId: id,
          }));
        }

        const resultMessage = response.data;

        if (!resultMessage) {
          throw new Error('Did not receive a result message.');
        }

        conversationStore.acknowledgeMessage(resultMessage, 'sent', messageAtSend);

        // Remove the sending status after a while
        setTimeout(() => {
          const message = getConversationMessage.value(
            conversationId,
            id,
          );

          if (message?.status !== 'sent') {
            return;
          }

          message.status = undefined;
          conversationStore.updateMessage(message);
        }, 3000);
      }
      catch (error) {
        const currentMessage = getConversationMessage.value(conversationId, id);
        if (!currentMessage || currentMessage.message.deletedAt || currentMessage.status !== 'sending') {
          // An SSE message may already have acknowledged delivery.
          return;
        }

        currentMessage.status = 'error';
        appLogger.error({
          message: 'Could not send chat message',
          context: { conversationId, messageId: id },
          sensitiveContext: { error },
        });
      }
    }

    /** Retries the original client UUID so a lost response cannot cause a duplicate message. */
    async function retryMessage(message: MessageState): Promise<void> {
      const messageState = getConversationMessage.value(
        message.message.conversationId,
        message.message.id,
      );
      if (messageState?.status !== 'error' || messageState.message.deletedAt) {
        return;
      }

      messageState.needsRetryOrdering = true;
      await sendMessage(messageState);
    }

    async function editMessage(message: string): Promise<void> {
      if (!editingMessage.value) {
        throw new Error('No edited messages');
      }

      const { oldMessage } = editingMessage.value;

      const payload: MessageUpdatePayload = {
        message,
      };

      const updatedMessage = { ...oldMessage };
      updatedMessage.message.message = message;
      updatedMessage.message.isEdited = true;
      editingMessage.value = undefined;

      await api.conversationMessage.updateConversationMessage({
        conversationId: updatedMessage.message.conversationId,
        messageId: updatedMessage.message.id,
        messageUpdatePayload: payload,
      });
    }

    function isEditingConversation(conversationId: string): boolean {
      return editingMessage.value?.oldMessage.message.conversationId === conversationId;
    }

    async function setMessageIsInView(
      messageToUpdate: MessageState,
      isInView: boolean,
    ): Promise<void> {
      const conversationToUpdate = conversationStore.getConversation(
        messageToUpdate.message.conversationId,
      );

      if (!conversationToUpdate) {
        throw new Error('Could not find the message to update.');
      }

      if (!user.value) {
        throw new Error('Could not find the user to update.');
      }

      messageToUpdate.isInView = isInView;

      const alreadyRead = messageToUpdate.message.isRead;

      if (isInView && !alreadyRead) {
        if (markAsReadTimers[messageToUpdate.message.id]) {
          clearTimeout(markAsReadTimers[messageToUpdate.message.id]);
        }

        markAsReadTimers[messageToUpdate.message.id] = setTimeout(() => {
          if (messageToUpdate.isInView) {
            markMessageAsRead(messageToUpdate);
          }
        }, MARK_AS_READ_TIMEOUT);
      }
    }

    async function markMessageAsRead(messageState: MessageState): Promise<void> {
      if (!user.value) {
        throw new Error('Could not find the user to update.');
      }

      await api.conversationMessage.markConversationMessageAsRead({
        conversationId: messageState.message.conversationId,
        messageId: messageState.message.id,
      });

      messageState.message.isRead = true;
    }

    async function addReadMessageStatus(
      conversationId: string,
      messageId: string,
      readMessagesCount: number,
    ): Promise<void> {
      const messageState = getConversationMessage.value(
        conversationId,
        messageId,
      );

      if (!messageState) {
        console.error(
          `Could not find the message to add message read status to  (conversationId: ${conversationId}, messageId: ${messageId}), failing silently...`,
        );

        return;
      }

      messageState.message.readMessagesCount = readMessagesCount;
    }

    function updateReaction(
      messageState: MessageState,
      reaction: Pick<ReactionResultDTO, 'previousReaction' | 'reaction'>,
    ): void {
      const reactionsMap = new Map(
        messageState.message.reactions?.map(({ reaction, count }) => [
          reaction,
          count,
        ]),
      );

      if (reaction.previousReaction) {
        const previousReactionCount
          = reactionsMap.get(reaction.previousReaction) ?? 0;
        if (previousReactionCount <= 1) {
          reactionsMap.delete(reaction.previousReaction);
        }
        else {
          reactionsMap.set(
            reaction.previousReaction,
            previousReactionCount - 1,
          );
        }
      }

      const newReactionCount = reactionsMap.get(reaction.reaction) ?? 0;
      reactionsMap.set(reaction.reaction, newReactionCount + 1);

      messageState.message.reactions = Array.from(reactionsMap.entries()).map(
        ([reaction, count]) => ({
          reaction,
          count,
        }),
      );
    }

    function updateMessageReactions(
      messageState: MessageState,
      aggregatedReactions: ReactionAggregateResultDTO[],
    ): void {
      if (!messageState.message.reactions) {
        messageState.message.reactions = [];
      }

      const isAtBottom = scrollStore.isScrollPositionAtBottom(
        messageState.message.conversationId,
      );

      messageState.message.reactions = aggregatedReactions;

      if (isAtBottom) {
        scrollStore.scrollConversationToBottom(
          messageState.message.conversationId,
          true,
        );
      }
    }

    function addReaction(messageState: MessageState, reactionToAdd: string): void {
      if (!messageState.message.reactions) {
        messageState.message.reactions = [];
      }

      const isAtBottom = scrollStore.isScrollPositionAtBottom(
        messageState.message.conversationId,
      );

      const reactionsMap = new Map(
        messageState.message.reactions?.map(({ reaction, count }) => [
          reaction,
          count,
        ]),
      );

      const reactionCount = reactionsMap.get(reactionToAdd) ?? 0;
      reactionsMap.set(reactionToAdd, reactionCount + 1);
      messageState.message.reactions = Array.from(reactionsMap.entries()).map(
        ([reaction, count]) => ({
          reaction,
          count,
        }),
      );

      if (isAtBottom) {
        scrollStore.scrollConversationToBottom(
          messageState.message.conversationId,
          true,
        );
      }
    }

    function deleteReaction(
      conversationId: string,
      messageId: string,
      reactionString: string,
    ): void {
      const messageState = getConversationMessage.value(
        conversationId,
        messageId,
      );

      if (!messageState) {
        return;
      }

      messageState.message.reactions = messageState.message.reactions
        ?.map((reactionAggregate) => {
          if (reactionAggregate.reaction === reactionString) {
            const newCount = reactionAggregate.count - 1;
            return {
              ...reactionAggregate,
              count: newCount,
            };
          }

          return reactionAggregate;
        })
        .filter(reactionAggregate => reactionAggregate.count > 0);
    }

    async function sendMessageReaction(
      messageState: MessageState,
      reactionString: string,
    ): Promise<void> {
      if (!user.value) {
        throw new Error('Tried to react without a loaded user.');
      }

      const userId = user.value.id;
      const existingReaction = messageState.message.ownReaction;

      const { conversationId, id: messageId } = messageState.message;

      if (pendingReactionMessageIds.has(messageId)) {
        return;
      }

      const handleAddReaction = async (): Promise<void> => {
        addReaction(messageState, reactionString);
        messageState.message.ownReaction = reactionString;

        const reactionPayload: ReactionCreatePayload = {
          reaction: reactionString,
        };

        const response = await api.unwrap(
          api.conversationMessage.reactToConversationMessage({
            conversationId,
            messageId,
            reactionCreatePayload: reactionPayload,
          }),
        );

        const data = response.data;
        if (!data) {
          throw new Error('Did not receive a response from the server.');
        }
      };

      const handleUpdateReaction = async (existingReactionString: string): Promise<void> => {
        if (!user.value) {
          throw new Error('Tried to update reaction without a loaded user.');
        }
        const reactionPayload: ReactionCreatePayload = {
          reaction: reactionString,
        };

        updateReaction(messageState, {
          reaction: reactionString,
          previousReaction: existingReactionString,
        });
        messageState.message.ownReaction = reactionString;

        const response = await api.unwrap(
          api.conversationMessage.updateConversationMessageReaction({
            conversationId,
            messageId,
            reactionCreatePayload: reactionPayload,
          }),
        );

        const data = response.data;

        if (!data) {
          throw new Error('Did not receive a response from the server.');
        }
      };

      const handleDeleteReaction = async (reactionToDelete: string): Promise<void> => {
        deleteReaction(conversationId, messageId, reactionToDelete);
        messageState.message.ownReaction = undefined;

        await api.conversationMessage.deleteConversationMessageReactions({
          conversationId,
          messageId,
        });
      };

      pendingReactionMessageIds.add(messageId);
      try {
        if (existingReaction === reactionString) {
          await handleDeleteReaction(existingReaction);
        }
        else if (existingReaction) {
          await handleUpdateReaction(existingReaction);
        }
        else {
          await handleAddReaction();
        }
      }
      catch (error) {
        try {
          const reactionsBeforeRecovery = messageState.message.reactions;
          const result = await api.unwrap(
            api.conversationMessage.getMessageReactions({ conversationId, messageId }),
          );
          if (!result.data) {
            throw new Error('Did not receive reactions from the server.');
          }

          if (messageState.message.reactions === reactionsBeforeRecovery) {
            updateMessageReactions(messageState, aggregateReactions(result.data));
          }
          messageState.message.ownReaction = result.data.find(reaction => reaction.sentByUserId === userId)?.reaction;
        }
        catch (recoveryError) {
          console.error('Error reloading reactions after a failed write:', recoveryError);
        }
        throw error;
      }
      finally {
        pendingReactionMessageIds.delete(messageId);
      }
    }

    function updateMessage(
      messageToChange: MessageInput,
      updatedMessage: MessageResultDTO,
    ): void {
      const messageState = messageToState(messageToChange);

      if (!messageState) {
        throw new Error('Could not find the message to update.');
      }

      messageState.message = convertToMessageResultV2({
        ...updatedMessage,
      });
    }

    async function deleteMessage(messageState: MessageState): Promise<void> {
      conversationStore.deleteMessageFromConversation(
        messageState.message.conversationId,
        messageState.message.id,
      );

      await api.conversationMessage.deleteConversationMessage({
        conversationId: messageState.message.conversationId,
        messageId: messageState.message.id,
      });
    }

    function messageToState(message: MessageInput): MessageState | undefined {
      if ('id' in message) {
        return getConversationMessage.value(message.conversationId, message.id);
      }

      return message;
    }

    return {
      activeMessageActionsId,
      editingMessage,
      quoteMessage,
      getConversationMessage,
      openContextMenuId,
      pendingReactionMessageIds,
      setMessageIsInView,
      markMessageAsRead,
      updateReaction,
      updateMessageReactions,
      addReaction,
      deleteReaction,
      sendMessageReaction,
      addReadMessageStatus,
      deleteMessage,
      createMessage,
      retryMessage,
      editMessage,
      isEditingConversation,
      setActiveMessageActionsId,
      setOpenContextMenuId,
      updateMessage,
    };
  },
);
